import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { ClassStudentSelect, TargetModeSelect } from "@/components/ClassStudentSelect";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { colorOf, EXAM_TRACK_GRADES } from "@/lib/subjects";
import { toast } from "sonner";
import { ClipboardCheck, Plus, Pencil, Trash2, X, ChevronDown, ChevronRight, Hourglass } from "lucide-react";

const todayStr = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");
const fmtNet = (n) => Number((n ?? 0).toFixed(2)).toLocaleString("tr-TR");
const EMPTY = { panel: "", section: "", name: "", date: todayStr(), soru: "", dogru: "", yanlis: "", bos: "" };

const PANELS = [
  { key: "turkce", label: "Türkçe", color: "amber", sections: [
    { key: "deyim_atasozu", label: "Deyim ve Atasözü" },
    { key: "gorsel_okuma", label: "Görsel Okuma" },
    { key: "dil_bilgisi", label: "Dil Bilgisi" },
    { key: "paragraf", label: "Paragraf" },
    { key: "genel", label: "Genel Türkçe" },
  ]},
  { key: "matematik", label: "Matematik", color: "sky", sections: [
    { key: "temel", label: "Temel Matematik" },
    { key: "donem_uniteleri", label: "Dönem Üniteleri" },
    { key: "zoru_sevenler", label: "Zoru Sevenler" },
  ]},
  { key: "fen", label: "Fen Bilimleri", color: "emerald", sections: [] },
  { key: "inkilap", label: "İnkılap Tarihi", color: "rose", sections: [] },
  { key: "ingilizce", label: "İngilizce", color: "indigo", sections: [] },
  { key: "din", label: "Din Kültürü", color: "purple", sections: [] },
];
const panelOf = (k) => PANELS.find((p) => p.key === k);

export default function SinavTakip() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [studentId, setStudentId] = useState(params.get("student") || null);
  const [mode, setMode] = useState(params.get("grade") ? "class" : "student");
  const [clsGrade, setClsGrade] = useState(Number(params.get("grade")) || 4);
  const [clsSection, setClsSection] = useState(params.get("section") || "");
  const [studentName, setStudentName] = useState(params.get("name") || "");
  const [students, setStudents] = useState([]);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [openPanels, setOpenPanels] = useState(new Set(PANELS.map((p) => p.key)));

  const canEdit = user?.role === "admin" || user?.role === "koc";

  useEffect(() => {
    if (user?.role === "ogrenci" || user?.role === "veli") {
      api.get("/my/context").then(({ data }) => { setStudentId(data.student_id); setStudentName(data.student_name || ""); });
    } else if (canEdit) {
      api.get("/students").then(({ data }) => setStudents(data.filter((s) => EXAM_TRACK_GRADES.includes(s.grade))));
    }
  }, [user, canEdit]);

  useEffect(() => {
    if (!canEdit || !studentId) return;
    const st = students.find((s) => s.id === studentId);
    if (st) setStudentName(st.name);
  }, [canEdit, studentId, students]);

  const load = useCallback(async () => {
    if (!studentId) return;
    const { data } = await api.get("/sinav-takip", { params: { student_id: studentId } });
    setItems(data);
  }, [studentId]);
  useEffect(() => { load(); }, [load]);

  const togglePanel = (k) => setOpenPanels((prev) => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const resetForm = () => { setForm(EMPTY); setEditId(null); };

  const setField = (k, v) => {
    setForm((f) => {
      const n = { ...f, [k]: v };
      const s = Number(n.soru) || 0;
      if (s > 0 && (k === "soru" || k === "dogru" || k === "yanlis")) {
        n.bos = String(Math.max(0, s - (Number(n.dogru) || 0) - (Number(n.yanlis) || 0)));
      }
      return n;
    });
  };

  const save = async () => {
    if (!form.panel) return toast.error("Ders paneli seçin");
    const p = panelOf(form.panel);
    if (p?.sections.length && !form.section) return toast.error("Bölüm seçin");
    if (mode === "class") {
      try {
        const { data } = await api.post("/sinav-takip/assign-class", {
          grade: clsGrade, class_section: clsSection || null, panel: form.panel, section: form.section, name: form.name, date: form.date,
          soru: Number(form.soru) || 0, dogru: Number(form.dogru) || 0, yanlis: Number(form.yanlis) || 0, bos: Number(form.bos) || 0,
        });
        toast.success(`${data.count} öğrenciye sınav kaydı eklendi ✓`);
        resetForm();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      return;
    }
    if (!studentId) return toast.error("Önce öğrenci seçin");
    const payload = {
      student_id: studentId, panel: form.panel, section: form.section, name: form.name, date: form.date,
      soru: Number(form.soru) || 0, dogru: Number(form.dogru) || 0, yanlis: Number(form.yanlis) || 0, bos: Number(form.bos) || 0,
    };
    try {
      if (editId) { await api.put(`/sinav-takip/${editId}`, payload); toast.success("Sınav güncellendi ✓"); }
      else { await api.post("/sinav-takip", payload); toast.success("Sınav eklendi ✓"); }
      resetForm(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const startEdit = (it) => {
    setEditId(it.id);
    setForm({ panel: it.panel, section: it.section || "", name: it.name || "", date: it.date, soru: String(it.soru ?? ""), dogru: String(it.dogru), yanlis: String(it.yanlis), bos: String(it.bos) });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Bu sınav kaydı silinsin mi?")) return;
    try { await api.delete(`/sinav-takip/${id}`); toast.success("Silindi"); if (editId === id) resetForm(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const grouped = useMemo(() => {
    const m = {};
    items.forEach((i) => {
      const pk = i.panel || "turkce";
      const sk = i.section || "";
      (m[pk] = m[pk] || {});
      (m[pk][sk] = m[pk][sk] || []).push(i);
    });
    return m;
  }, [items]);

  const formSections = panelOf(form.panel)?.sections || [];

  if (!canEdit && !studentId) return <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">Bağlı öğrenci bulunamadı.</div>;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-100 text-rose-600"><ClipboardCheck className="h-6 w-6" /></div>
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Sınav Takip Paneli</h1>
          <p className="text-slate-500">4 &amp; 7. sınıf — ders ve bölüm bazlı sınav sonuç tabloları{studentName ? ` · ${studentName}` : ""}</p>
        </div>
      </div>

      {canEdit && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <label className="mb-1 block text-sm font-semibold text-slate-700">Öğrenci (yalnızca 4 &amp; 7. sınıf)</label>
          <TargetModeSelect students={students} mode={mode} setMode={setMode} studentId={studentId} setStudentId={(v) => { setStudentId(v || null); resetForm(); }} grade={clsGrade} setGrade={setClsGrade} section={clsSection} setSection={setClsSection} testid="st-student-select" />
          {students.length === 0 && <p className="mt-2 text-sm text-amber-600">4 veya 7. sınıf öğrencisi bulunamadı.</p>}
        </div>
      )}

      {(studentId || (canEdit && mode === "class")) && canEdit && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
              {editId ? <><Pencil className="h-5 w-5 text-indigo-500" /> Sınavı Düzenle</> : <><Plus className="h-5 w-5 text-indigo-500" /> Yeni Sınav</>}
            </h3>
            {editId && <button data-testid="st-cancel-edit-btn" onClick={resetForm} className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-semibold text-slate-500 transition hover:bg-slate-100"><X className="h-4 w-4" /> Vazgeç</button>}
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div>
              <label className={lbl}>Ders Paneli</label>
              <select data-testid="st-form-panel" value={form.panel} onChange={(e) => setForm({ ...form, panel: e.target.value, section: "" })} className={inp}>
                <option value="">Seçin…</option>
                {PANELS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
            {formSections.length > 0 && (
              <div>
                <label className={lbl}>Bölüm</label>
                <select data-testid="st-form-section" value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} className={inp}>
                  <option value="">Seçin…</option>
                  {formSections.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className={lbl}>Sınav Adı</label>
              <input data-testid="st-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="ör. 1. Deneme" className={inp} />
            </div>
            <div>
              <label className={lbl}>Tarih</label>
              <input data-testid="st-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inp} />
            </div>
            <NumField testid="st-soru-input" label="Soru" value={form.soru} onChange={(e) => setField("soru", e.target.value)} />
            <NumField testid="st-dogru-input" label="Doğru" value={form.dogru} onChange={(e) => setField("dogru", e.target.value)} />
            <NumField testid="st-yanlis-input" label="Yanlış" value={form.yanlis} onChange={(e) => setField("yanlis", e.target.value)} />
            <NumField testid="st-bos-input" label="Boş" value={form.bos} onChange={(e) => setField("bos", e.target.value)} />
            <div className="flex items-end">
              <button data-testid="st-save-btn" onClick={save} className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2.5 font-bold text-white transition hover:bg-indigo-700 active:scale-95">
                {editId ? <><Pencil className="h-4 w-4" /> Güncelle</> : <><Plus className="h-4 w-4" /> Kaydet</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {studentId && (
        <div className="space-y-4">
          {PANELS.map((p) => {
            const c = colorOf(p.color);
            const open = openPanels.has(p.key);
            const panelItems = grouped[p.key] || {};
            const allRows = Object.values(panelItems).flat();
            const totalNet = allRows.reduce((s, r) => s + (r.net || 0), 0);
            const sectionList = p.sections.length ? p.sections : [{ key: "", label: p.label }];
            return (
              <div key={p.key} data-testid={`st-panel-${p.key}`} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <button data-testid={`st-panel-toggle-${p.key}`} onClick={() => togglePanel(p.key)} className={`flex w-full items-center gap-2 px-5 py-3.5 text-left transition ${c.bg}`}>
                  {open ? <ChevronDown className="h-5 w-5 text-slate-500" /> : <ChevronRight className="h-5 w-5 text-slate-500" />}
                  <span className={`h-3 w-3 rounded-full ${c.dot}`} />
                  <span className={`font-display text-lg font-bold ${c.text}`}>{p.label}</span>
                  <span className="ml-auto text-sm font-semibold text-slate-600">{allRows.length} kayıt · Net <b className="text-indigo-700">{fmtNet(totalNet)}</b></span>
                </button>
                {open && (
                  <div className="space-y-3 p-4">
                    {sectionList.map((sec) => {
                      const rows = panelItems[sec.key] || [];
                      return (
                        <div key={sec.key || "main"} data-testid={`st-section-${p.key}-${sec.key || "main"}`} className="rounded-xl border border-slate-200">
                          {p.sections.length > 0 && (
                            <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2.5">
                              <span className="text-sm font-bold text-slate-800">{sec.label}</span>
                              <span className="ml-auto text-xs font-semibold text-slate-400">{rows.length} kayıt</span>
                            </div>
                          )}
                          {rows.length === 0 ? (
                            <p className="flex items-center justify-center gap-2 py-6 text-sm text-slate-400">
                              <Hourglass className="h-4 w-4" /> İçerik bekleniyor — henüz kayıt yok.
                            </p>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full min-w-[520px] text-sm">
                                <thead>
                                  <tr className="border-b border-slate-200 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                                    <th className="px-4 py-2.5">Sınav</th>
                                    <th className="px-3 py-2.5">Tarih</th>
                                    <th className="px-3 py-2.5 text-center">Soru</th>
                                    <th className="px-3 py-2.5 text-center">Doğru</th>
                                    <th className="px-3 py-2.5 text-center">Yanlış</th>
                                    <th className="px-3 py-2.5 text-center">Boş</th>
                                    <th className="px-3 py-2.5 text-center">Net</th>
                                    {canEdit && <th className="px-4 py-2.5 text-right">İşlem</th>}
                                  </tr>
                                </thead>
                                <tbody>
                                  {rows.map((it) => (
                                    <tr key={it.id} data-testid={`st-row-${it.id}`} className={`border-b border-slate-100 transition hover:bg-slate-50 ${editId === it.id ? "bg-indigo-50/60" : ""}`}>
                                      <td className="px-4 py-3 font-semibold text-slate-900">{it.name || "Sınav"}</td>
                                      <td className="px-3 py-3 text-slate-600">{fmtDate(it.date)}</td>
                                      <td className="px-3 py-3 text-center font-semibold text-slate-700">{it.soru}</td>
                                      <td className="px-3 py-3 text-center font-semibold text-emerald-600">{it.dogru}</td>
                                      <td className="px-3 py-3 text-center font-semibold text-rose-600">{it.yanlis}</td>
                                      <td className="px-3 py-3 text-center text-slate-500">{it.bos}</td>
                                      <td className="px-3 py-3 text-center font-bold text-indigo-600">{fmtNet(it.net)}</td>
                                      {canEdit && (
                                        <td className="px-4 py-3 text-right">
                                          <div className="flex justify-end gap-1.5">
                                            <button data-testid={`st-edit-${it.id}`} onClick={() => startEdit(it)} className="rounded-lg p-2 text-slate-400 transition hover:bg-indigo-50 hover:text-indigo-600"><Pencil className="h-4 w-4" /></button>
                                            <button data-testid={`st-delete-${it.id}`} onClick={() => remove(it.id)} className="rounded-lg p-2 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                                          </div>
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {canEdit && !studentId && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500">Sonuç girmek için yukarıdan bir öğrenci seçin.</div>
      )}
    </div>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className={lbl}>{label}</label>
      <input data-testid={testid} type="number" min={0} value={value} onChange={onChange} placeholder="0" className={inp} />
    </div>
  );
}

const lbl = "mb-1 block text-xs font-semibold text-slate-600";
const inp = "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100";
